import { useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useCreateDocumentKind, useDocumentKinds, useUpdateDocumentKind } from './kindHooks';
import { humanizeDocumentKind } from './kindLabels';

/**
 * Per-tenant document-kind management. Lists kinds, adds, renames, deactivates
 * (soft, never hard-deleted to protect referential history), and reorders via
 * SortOrder. Rendered on the Settings page (v1.3 Item 7).
 */
export function DocumentKindsSettings() {
  const { data: kinds } = useDocumentKinds(true);
  const create = useCreateDocumentKind();
  const update = useUpdateDocumentKind();

  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ordered = (kinds ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  const onAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await create.mutateAsync({ name });
      setNewName('');
    } catch {
      setError('Could not add that kind (it may already exist).');
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= ordered.length) return;
    const a = ordered[index];
    const b = ordered[target];
    // Swap sort orders.
    await update.mutateAsync({ id: a.id, sortOrder: b.sortOrder });
    await update.mutateAsync({ id: b.id, sortOrder: a.sortOrder });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Document kinds</h2>
        <p className="text-sm text-slate-500 mt-1">
          The selectable kinds when uploading a template. Deactivated kinds stay on existing
          documents but no longer appear in the dropdown.
        </p>
      </div>

      <ul className="divide-y divide-slate-100">
        {ordered.map((k, i) => (
          <KindRow
            key={k.id}
            id={k.id}
            name={k.name}
            isActive={k.isActive}
            canMoveUp={i > 0}
            canMoveDown={i < ordered.length - 1}
            busy={update.isPending}
            onMoveUp={() => move(i, -1)}
            onMoveDown={() => move(i, 1)}
            onRename={(name) => update.mutate({ id: k.id, name })}
            onToggleActive={() => update.mutate({ id: k.id, isActive: !k.isActive })}
          />
        ))}
      </ul>

      <div className="flex items-center gap-2 pt-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onAdd();
          }}
          placeholder="New kind name…"
          className="flex-1 border border-slate-300 rounded px-3 py-2 min-h-[44px]"
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={!newName.trim() || create.isPending}
          className="inline-flex items-center gap-1 bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
        >
          <Plus size={14} /> Add
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function KindRow({
  name,
  isActive,
  canMoveUp,
  canMoveDown,
  busy,
  onMoveUp,
  onMoveDown,
  onRename,
  onToggleActive
}: {
  id: string;
  name: string;
  isActive: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  busy: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: (name: string) => void;
  onToggleActive: () => void;
}) {
  const [draft, setDraft] = useState(name);
  const dirty = draft.trim() !== name && draft.trim().length > 0;

  return (
    <li className="flex items-center gap-2 py-2">
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp || busy}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
          aria-label="Move up"
        >
          <ChevronUp size={14} />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown || busy}
          className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
          aria-label="Move down"
        >
          <ChevronDown size={14} />
        </button>
      </div>

      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className={`flex-1 border border-slate-300 rounded px-3 py-2 min-h-[40px] ${
          isActive ? '' : 'text-slate-400'
        }`}
        // Built-in kinds display friendly labels; renaming stores the typed text.
        placeholder={humanizeDocumentKind(name)}
      />

      {dirty && (
        <button
          type="button"
          onClick={() => onRename(draft.trim())}
          className="text-sm px-2 py-1 rounded border border-sky-300 bg-sky-50 text-sky-700"
        >
          Save
        </button>
      )}

      <button
        type="button"
        onClick={onToggleActive}
        className={`text-xs px-2 py-1 rounded border ${
          isActive
            ? 'border-slate-300 bg-slate-50 text-slate-600'
            : 'border-sky-300 bg-sky-50 text-sky-700'
        }`}
      >
        {isActive ? 'Deactivate' : 'Reactivate'}
      </button>
    </li>
  );
}
